/* Sprint 2R-4A — Admin panel: Vognmand onboarding review + invoice review. */
import React from "react";
import { adminApi } from "../../lib/api";
import { Loader2, CheckCircle2, XCircle, AlertCircle, Eye, FileText, Building2, Briefcase, ShieldCheck, ScrollText, Download } from "lucide-react";
import { toast } from "sonner";

function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return e?.message || "Fejl";
}

const STATUS_STYLE = {
  not_started: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-50 text-blue-700",
  submitted: "bg-amber-50 text-amber-800",
  needs_more_info: "bg-orange-50 text-orange-800",
  approved: "bg-emerald-50 text-emerald-800",
  rejected: "bg-red-50 text-red-700",
  blocked: "bg-red-100 text-red-900",
};

export default function VognmandOnboardingAdminPanel() {
  const [tab, setTab] = React.useState("onboarding"); // onboarding | invoices
  return (
    <div className="space-y-6" data-testid="vognmand-onboarding-admin-panel">
      <div>
        <h2 className="font-display font-bold text-xl text-brand-dark flex items-center gap-2">
          <Building2 size={20} className="text-brand-red"/> Vognmand onboarding & faktura-review
        </h2>
        <p className="text-sm text-brand-text2 mt-1">Compliance gate for ruter — godkend underleverandøraftale + DKK-fakturaer.</p>
      </div>
      <div className="flex gap-2 border-b border-brand">
        {[
          ["onboarding", "Onboarding pending", "tab-vognmand-onboarding"],
          ["payments", "Betalingsændringer", "tab-vognmand-payments"],
          ["invoices", "Faktura-review (DKK)", "tab-vognmand-invoices"],
        ].map(([k, lbl, tid]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={tid}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2 hover:text-brand-dark"}`}>
            {lbl}
          </button>
        ))}
      </div>
      {tab === "onboarding" && <OnboardingList />}
      {tab === "payments" && <PaymentChangesList />}
      {tab === "invoices" && <InvoicesList />}
    </div>
  );
}

function OnboardingList() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openId, setOpenId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/admin/vognmand/onboarding/pending");
      setItems(r.data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser...</div>;
  if (items.length === 0) return <div className="bg-emerald-50 border border-emerald-200 p-6 text-center text-emerald-800 font-semibold">Ingen vognmænd venter på onboarding-godkendelse 🎉</div>;

  return (
    <div>
      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm" data-testid="vognmand-onboarding-table">
          <thead>
            <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
              <th className="p-3">Firma</th>
              <th className="p-3">CVR</th>
              <th className="p-3">Status</th>
              <th className="p-3">Aftale</th>
              <th className="p-3">Reg/konto (masked)</th>
              <th className="p-3">Konflikt review</th>
              <th className="p-3">Blokeringer</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.subcontractor_id} className="border-b border-gray-100 hover:bg-brand-bg2" data-testid={`vognmand-onb-row-${it.subcontractor_id}`}>
                <td className="p-3 font-semibold">{it.company_name}</td>
                <td className="p-3 text-xs">{it.cvr}</td>
                <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-1 ${STATUS_STYLE[it.onboarding_status] || ""}`}>{it.onboarding_status}</span></td>
                <td className="p-3 text-xs">{it.agreement_signed ? <CheckCircle2 size={14} className="text-emerald-600 inline"/> : <XCircle size={14} className="text-red-500 inline"/>} {it.agreement_version_signed || "—"}</td>
                <td className="p-3 text-xs">{it.bank_reg || "—"} / {it.bank_account_masked || "—"}</td>
                <td className="p-3 text-xs">{it.conflict_competitor_relationship ? <span className="text-amber-700 font-semibold">⚠ ja</span> : "nej"}</td>
                <td className="p-3 text-xs text-brand-text2">{(it.blocking_reasons || []).length} stk</td>
                <td className="p-3">
                  <button onClick={() => setOpenId(it.subcontractor_id)}
                          className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2 inline-flex items-center gap-1"
                          data-testid={`vognmand-onb-open-${it.subcontractor_id}`}>
                    <Eye size={12}/> Gennemgå
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openId && <OnboardingDetailModal sc_id={openId} onClose={() => setOpenId(null)} onChange={load}/>}
    </div>
  );
}

function OnboardingDetailModal({ sc_id, onClose, onChange }) {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [adminNote, setAdminNote] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await adminApi().get(`/admin/vognmand/${sc_id}/onboarding`);
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [sc_id]);

  React.useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const decision = async (action) => {
    if (action !== "approve" && reason.trim().length < 4) return toast.error("Begrundelse skal være min. 4 tegn");
    setBusy(true);
    try {
      if (action === "approve") {
        await adminApi().post(`/admin/vognmand/${sc_id}/onboarding/approve`,
          { admin_note: adminNote || undefined });
        toast.success("Vognmand godkendt");
      } else {
        await adminApi().post(`/admin/vognmand/${sc_id}/onboarding/${action}`,
          { reason: reason.trim() });
        toast.success(`Vognmand ${action === "reject" ? "afvist" : action === "block" ? "blokeret" : "anmodet om mere info"}`);
      }
      onChange();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await adminApi().get(`/admin/vognmand/${sc_id}/onboarding/agreement-pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `underleverandoeraftale-${sc_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl mx-4 mb-8 border border-brand" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{data.company_name}</h3>
          <button onClick={onClose} data-testid="vognmand-onb-detail-close"><XCircle size={18}/></button>
        </div>
        <div className="p-5 space-y-5" data-testid="vognmand-onb-detail">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <Section title="Status" Icon={Building2}>
              <Row k="Status" v={<span className={`text-[10px] uppercase font-bold px-2 py-1 ${STATUS_STYLE[data.onboarding_status] || ""}`}>{data.onboarding_status}</span>}/>
              <Row k="Aftale signed" v={data.checks?.agreement_signed ? "✅" : "❌"}/>
              <Row k="Aftale version" v={data.agreement_version_signed || "—"}/>
              <Row k="Underskrevet" v={data.agreement_signed_at ? new Date(data.agreement_signed_at).toLocaleString("da-DK") : "—"}/>
              <Row k="Blokeringer" v={(data.blocking_reasons || []).join(", ") || "ingen"}/>
              <Row k="Admin review krævet" v={data.admin_review_required ? "⚠ ja" : "nej"}/>
              <Row k="Godkendte køretøjer" v={
                <span className={data.approved_vehicle_count >= 1 || data.vehicle_requirement_exempt ? "text-emerald-700" : "text-amber-700"}>
                  {data.approved_vehicle_count ?? 0}{data.vehicle_requirement_exempt ? " · FRITAGET" : ""}
                </span>}/>
            </Section>
            <Section title="Underskriver" Icon={ScrollText}>
              <Row k="Fulde navn" v={data.signatory?.full_name || "—"}/>
              <Row k="Rolle" v={data.signatory?.role_title || "—"}/>
              <Row k="Email" v={data.signatory?.email || "—"}/>
              <Row k="Tlf" v={data.signatory?.phone || "—"}/>
              <Row k="IP" v={data.agreement_signature_proof?.ip || "—"}/>
              <Row k="Typed name" v={data.agreement_signature_proof?.typed_full_name || "—"}/>
            </Section>
            <Section title="Bank (admin only)" Icon={Briefcase}>
              <Row k="Reg.nr." v={data.bank_reg || "—"}/>
              <Row k="Konto (maskeret)" v={data.bank_account_masked || "—"}/>
              <Row k="Konto (fuld)" v={data.bank_account_full || "—"}/>
            </Section>
            <Section title="Forsikring" Icon={ShieldCheck}>
              {["vehicle_liability", "work_injury", "transport_liability"].map((k) => (
                <Row key={k} k={k.replace(/_/g, " ")} v={data.insurance_confirmation?.[k] ? "✅" : "❌"}/>
              ))}
              <Row k="Bekræftet" v={data.insurance_confirmation?.confirmed_at ? new Date(data.insurance_confirmation.confirmed_at).toLocaleDateString("da-DK") : "—"}/>
            </Section>
          </div>
          <Section title="Interessekonflikt" Icon={AlertCircle}>
            <Row k="Erklæring afgivet" v={data.conflict_of_interest?.declared ? "✅" : "❌"}/>
            <Row k="Konkurrent-relation" v={data.conflict_of_interest?.competitor_relationship ? "⚠ ja" : "nej"}/>
            {data.conflict_of_interest?.competitor_company_name && (
              <>
                <Row k="Firma" v={data.conflict_of_interest.competitor_company_name}/>
                <Row k="Relationstype" v={data.conflict_of_interest.relationship_type}/>
              </>
            )}
          </Section>
          <Section title="Klausuler accepteret" Icon={CheckCircle2}>
            {Object.entries(data.accepted_clauses || {}).filter(([k]) => k.endsWith("_accepted")).map(([k, v]) => (
              <Row key={k} k={k.replace(/_/g, " ")} v={v ? "✅" : "❌"}/>
            ))}
          </Section>
          {data.checks?.agreement_signed && (
            <div>
              <button onClick={downloadPdf} className="px-4 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm inline-flex items-center gap-2"
                      data-testid="vognmand-onb-download-pdf">
                <Download size={14}/> Download underskrevet PDF
              </button>
            </div>
          )}
          <VehicleExemptToggle sc_id={sc_id} data={data} onChange={load}/>
          <details className="bg-brand-bg2 border border-brand p-3">
            <summary className="cursor-pointer text-sm font-bold">Audit log ({(data.audit_log || []).length})</summary>
            <div className="mt-2 space-y-1 max-h-60 overflow-y-auto text-xs" data-testid="vognmand-onb-audit-log">
              {(data.audit_log || []).map((a) => (
                <div key={a.id} className="border-b border-gray-200 py-1">
                  <b>{a.action}</b> · <span className="text-brand-text2">{a.actor_kind}</span> · {new Date(a.timestamp).toLocaleString("da-DK")}
                </div>
              ))}
            </div>
          </details>

          {data.onboarding_status === "approved" ? (
            <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              ✅ Vognmand er godkendt. Bruger separat reset-knap (admin endpoint) for at tvinge re-onboarding på ny aftale-version.
            </div>
          ) : (
            <div className="bg-brand-bg2 border border-brand p-4 space-y-3">
              <div>
                <label className="text-xs uppercase text-brand-text2 block mb-1">Admin note (valgfri ved godkendelse)</label>
                <input className="form-input w-full px-3 py-2 border border-brand text-sm"
                       value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                       data-testid="vognmand-onb-admin-note"/>
              </div>
              <div>
                <label className="text-xs uppercase text-brand-text2 block mb-1">Begrundelse (kræves ved afvis/block/request-info)</label>
                <textarea className="form-input w-full px-3 py-2 border border-brand text-sm" rows={2}
                          value={reason} onChange={(e) => setReason(e.target.value)}
                          data-testid="vognmand-onb-reason"/>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => decision("approve")} disabled={busy}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="vognmand-onb-approve">
                  ✅ Godkend
                </button>
                <button onClick={() => decision("reject")} disabled={busy}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="vognmand-onb-reject">
                  ❌ Afvis
                </button>
                <button onClick={() => decision("block")} disabled={busy}
                        className="px-4 py-2 bg-red-800 hover:bg-red-900 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="vognmand-onb-block">
                  🚫 Bloker
                </button>
                <button onClick={() => decision("request-info")} disabled={busy}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="vognmand-onb-request-info">
                  ℹ️ Bed om mere info
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2 flex items-center gap-1">
        <Icon size={12}/> {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-2 border-b border-gray-100 py-1 text-xs">
      <span className="text-brand-text2">{k}</span>
      <span className="font-semibold text-brand-dark text-right">{v}</span>
    </div>
  );
}

function VehicleExemptToggle({ sc_id, data, onChange }) {
  const [reason, setReason] = React.useState(data?.vehicle_exemption_reason || "");
  const [busy, setBusy] = React.useState(false);
  const exempt = !!data?.vehicle_requirement_exempt;
  const approvedCount = data?.approved_vehicle_count ?? 0;

  const submit = async (newExempt) => {
    if (newExempt && reason.trim().length < 4) return toast.error("Begrundelse skal være min. 4 tegn");
    setBusy(true);
    try {
      await adminApi().post(`/admin/vognmand/${sc_id}/vehicle-requirement/exempt`,
        { exempt: newExempt, reason: newExempt ? reason.trim() : "fjern_fritagelse" });
      toast.success(newExempt ? "Køretøjs-fritagelse aktiveret" : "Køretøjs-fritagelse fjernet");
      onChange();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Fejl");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-brand bg-white p-3 w-full" data-testid="vehicle-exempt-toggle">
      <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2 flex items-center justify-between">
        <span>Køretøjs-krav</span>
        <span className={`px-2 py-0.5 text-[10px] font-bold ${exempt ? "bg-blue-50 text-blue-800" : approvedCount >= 1 ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
          {exempt ? "FRITAGET" : approvedCount >= 1 ? `${approvedCount} godkendt(e)` : "MANGLER"}
        </span>
      </div>
      {!exempt && approvedCount < 1 && (
        <div className="text-[11px] text-amber-900 mb-2">
          ⚠ Vognmand mangler godkendt køretøj. Ruter blokeres indtil mindst ét køretøj godkendes, eller du fritager nedenfor.
        </div>
      )}
      {!exempt ? (
        <div className="space-y-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    rows={2} placeholder="Begrundelse for fritagelse (min. 4 tegn · audit log)"
                    className="form-input w-full px-3 py-2 border border-brand text-sm"
                    data-testid="vehicle-exempt-reason"/>
          <button onClick={() => submit(true)} disabled={busy}
                  className="text-xs px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white font-bold disabled:opacity-50"
                  data-testid="vehicle-exempt-grant">
            Fritag fra køretøjs-krav
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-[11px] text-blue-900">Begrundelse: {data?.vehicle_exemption_reason || "—"}</div>
          <button onClick={() => submit(false)} disabled={busy}
                  className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2 disabled:opacity-50"
                  data-testid="vehicle-exempt-revoke">
            Fjern fritagelse
          </button>
        </div>
      )}
    </div>
  );
}

function PaymentChangesList() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openId, setOpenId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/admin/vognmand/payment-changes/pending");
      setItems(r.data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-brand-text2"><Loader2 size={14} className="inline animate-spin"/> Indlæser...</div>;
  if (items.length === 0) return <div className="bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 text-sm" data-testid="payment-changes-empty">Ingen ventende betalingsændringer 🎉</div>;

  return (
    <div>
      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm" data-testid="payment-changes-table">
          <thead>
            <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
              <th className="p-3">Firma</th>
              <th className="p-3">CVR</th>
              <th className="p-3">Nuværende (masked)</th>
              <th className="p-3">Ny (masked)</th>
              <th className="p-3">IP</th>
              <th className="p-3">Indsendt</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.subcontractor_id} className="border-b border-gray-100 hover:bg-brand-bg2" data-testid={`payment-change-row-${it.subcontractor_id}`}>
                <td className="p-3 font-semibold">{it.company_name}</td>
                <td className="p-3 text-xs">{it.cvr}</td>
                <td className="p-3 text-xs">{describePm(it.old_masked)}</td>
                <td className="p-3 text-xs"><b>{describePm(it.new_masked)}</b></td>
                <td className="p-3 text-xs text-brand-text2">{it.submitted_ip || "—"}</td>
                <td className="p-3 text-xs text-brand-text2">{it.submitted_at ? new Date(it.submitted_at).toLocaleString("da-DK") : "—"}</td>
                <td className="p-3">
                  <button onClick={() => setOpenId(it.subcontractor_id)}
                          className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2"
                          data-testid={`payment-change-open-${it.subcontractor_id}`}>
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openId && <PaymentChangeDetailModal sc_id={openId} onClose={() => setOpenId(null)} onChange={load}/>}
    </div>
  );
}

function describePm(pm) {
  if (!pm) return "—";
  if (pm.type === "bank") return `Bank · ${pm.bank_reg_masked || "—"} / ${pm.bank_account_masked || "—"}`;
  if (pm.type === "fi") return `FI · ${pm.fi_code_masked || "—"}`;
  return "—";
}

function PaymentChangeDetailModal({ sc_id, onClose, onChange }) {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [reason, setReason] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await adminApi().get(`/admin/vognmand/${sc_id}/payment-details`);
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [sc_id]);

  React.useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const decide = async (action) => {
    if (action === "reject" && reason.trim().length < 4) return toast.error("Begrundelse skal være min. 4 tegn");
    setBusy(true);
    try {
      if (action === "approve") {
        await adminApi().post(`/admin/vognmand/${sc_id}/payment-change/approve`, {});
        toast.success("Betalingsoplysninger godkendt");
      } else {
        await adminApi().post(`/admin/vognmand/${sc_id}/payment-change/reject`, { reason: reason.trim() });
        toast.success("Ændring afvist — gamle oplysninger fortsætter");
      }
      onChange(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-8" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl mx-4 border border-brand" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{data.company_name} · CVR {data.cvr}</h3>
          <button onClick={onClose}><XCircle size={18}/></button>
        </div>
        <div className="p-5 space-y-4 text-sm" data-testid="payment-change-detail">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Nuværende (godkendt, aktiv)</div>
              <div className="bg-brand-bg2 border border-brand p-3 space-y-1">
                {data.approved_full ? (
                  <>
                    <Row k="Type" v={data.approved_full.type}/>
                    {data.approved_full.type === "bank" ? (
                      <>
                        <Row k="Reg.nr." v={data.approved_full.bank_reg}/>
                        <Row k="Konto (fuld)" v={data.approved_full.bank_account}/>
                        <Row k="Maskeret" v={data.approved_masked?.bank_account_masked}/>
                      </>
                    ) : (
                      <>
                        <Row k="FI-kode (fuld)" v={data.approved_full.fi_code}/>
                        <Row k="Maskeret" v={data.approved_masked?.fi_code_masked}/>
                      </>
                    )}
                  </>
                ) : <div className="text-brand-text2 text-xs">Ingen godkendt endnu</div>}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Ny (afventer godkendelse)</div>
              <div className="bg-amber-50 border border-amber-200 p-3 space-y-1">
                {data.pending_full ? (
                  <>
                    <Row k="Type" v={data.pending_full.type}/>
                    {data.pending_full.type === "bank" ? (
                      <>
                        <Row k="Reg.nr." v={data.pending_full.bank_reg}/>
                        <Row k="Konto (fuld)" v={data.pending_full.bank_account}/>
                      </>
                    ) : (
                      <Row k="FI-kode (fuld)" v={data.pending_full.fi_code}/>
                    )}
                    <Row k="Indsendt" v={data.pending_full.submitted_at ? new Date(data.pending_full.submitted_at).toLocaleString("da-DK") : "—"}/>
                    <Row k="IP" v={data.pending_full.submitted_ip}/>
                    <Row k="Bekræftelse" v={data.pending_full.confirmation_text?.slice(0, 60) + "…"}/>
                  </>
                ) : <div className="text-brand-text2 text-xs">Ingen ventende</div>}
              </div>
            </div>
          </div>

          {data.pending_full && (
            <div className="bg-brand-bg2 border border-brand p-4 space-y-3">
              <textarea className="form-input w-full px-3 py-2 border border-brand text-sm" rows={2}
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Begrundelse (kræves ved afvis · min. 4 tegn)"
                        data-testid="payment-change-reason"/>
              <div className="flex gap-2">
                <button onClick={() => decide("approve")} disabled={busy}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="payment-change-approve">✅ Godkend ny</button>
                <button onClick={() => decide("reject")} disabled={busy}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="payment-change-reject">❌ Afvis (behold gamle)</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function InvoicesList() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("pending_review");
  const [openId, setOpenId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi().get(`/admin/vognmand/invoices/v2${filter !== "all" ? `?status=${filter}` : ""}`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
                className="form-input px-3 py-2 border border-brand text-sm">
          <option value="pending_review">Afventer review</option>
          <option value="approved">Godkendt</option>
          <option value="rejected">Afvist</option>
          <option value="all">Alle</option>
        </select>
      </div>
      {loading ? <div><Loader2 className="animate-spin inline" size={14}/> Indlæser...</div> : (
        items.length === 0 ? <div className="bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 text-sm">Ingen fakturaer i denne status.</div> :
        <div className="bg-white border border-brand overflow-x-auto">
          <table className="w-full text-sm" data-testid="vognmand-invoices-table">
            <thead>
              <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
                <th className="p-3">Firma</th>
                <th className="p-3">Faktura</th>
                <th className="p-3">Periode</th>
                <th className="p-3 text-right">Beløb DKK</th>
                <th className="p-3">Match</th>
                <th className="p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-gray-100" data-testid={`vognmand-invoice-row-${it.id}`}>
                  <td className="p-3">
                    <div className="font-semibold">{it.subcontractor?.company_name}</div>
                    <div className="text-xs text-brand-text2">CVR {it.subcontractor?.cvr}</div>
                  </td>
                  <td className="p-3 text-xs">{it.invoice_number}<br/><span className="text-brand-text2">{it.invoice_date_iso}</span></td>
                  <td className="p-3 text-xs">{it.settlement_period}</td>
                  <td className="p-3 text-right">
                    <div className="font-semibold">{it.total_incl_vat_dkk?.toLocaleString("da-DK")} DKK</div>
                    <div className="text-xs text-brand-text2">ex {it.amount_ex_vat_dkk?.toLocaleString("da-DK")} + {it.vat_amount_dkk?.toLocaleString("da-DK")} moms</div>
                  </td>
                  <td className="p-3 text-xs">{it.matches_approved_settlement === true ? "✅" : it.matches_approved_settlement === false ? "⚠ nej" : "—"}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-1 ${STATUS_STYLE[it.status === "pending_review" ? "submitted" : it.status] || ""}`}>{it.status}</span></td>
                  <td className="p-3">
                    <button onClick={() => setOpenId(it.id)}
                            className="text-xs px-3 py-1 bg-white border border-brand hover:bg-brand-bg2"
                            data-testid={`vognmand-invoice-open-${it.id}`}>
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId && <InvoiceDetailModal invoice_id={openId} onClose={() => setOpenId(null)} onChange={load}/>}
    </div>
  );
}

function InvoiceDetailModal({ invoice_id, onClose, onChange }) {
  const [inv, setInv] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [reason, setReason] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const r = await adminApi().get(`/admin/vognmand/invoices/v2/${invoice_id}`);
      setInv(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [invoice_id]);

  React.useEffect(() => { load(); }, [load]);

  if (!inv) return null;

  const downloadPdf = async () => {
    try {
      const r = await adminApi().get(`/admin/vognmand/invoices/v2/${invoice_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = inv.filename || `faktura-${invoice_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const decide = async (decision) => {
    if (decision === "reject" && reason.trim().length < 4) return toast.error("Begrundelse skal være min. 4 tegn");
    setBusy(true);
    try {
      await adminApi().post(`/admin/vognmand/invoices/v2/${invoice_id}/decision`,
        { decision, reason: reason.trim() || undefined });
      toast.success(`Faktura ${decision === "approve" ? "godkendt" : "afvist"}`);
      onChange(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-8" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl mx-4 border border-brand" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{inv.invoice_number}</h3>
          <button onClick={onClose}><XCircle size={18}/></button>
        </div>
        <div className="p-5 space-y-4 text-sm" data-testid="vognmand-invoice-detail">
          <Row k="Vognmand" v={inv.subcontractor?.company_name}/>
          <Row k="CVR" v={inv.subcontractor?.cvr}/>
          <Row k="Momsregistreret" v={inv.subcontractor?.vat_registered ? "Ja" : "Nej"}/>
          <Row k="Faktura-dato" v={inv.invoice_date_iso}/>
          <Row k="Periode" v={inv.settlement_period}/>
          <Row k="Reg./konto" v={`${inv.bank_reg || "—"} / ${inv.bank_account_masked || "—"}`}/>
          <Row k="Beløb ex moms" v={`${inv.amount_ex_vat_dkk?.toLocaleString("da-DK")} DKK`}/>
          <Row k="Moms" v={`${inv.vat_amount_dkk?.toLocaleString("da-DK")} DKK (${inv.vat_rate_pct}%)`}/>
          <Row k="Total inkl. moms" v={`${inv.total_incl_vat_dkk?.toLocaleString("da-DK")} DKK`}/>
          <Row k="Betalingsfrist" v={`Netto ${inv.payment_terms_days} dage`}/>
          <Row k="Valuta" v={inv.currency}/>
          <Row k="Afregning total (EMA)" v={inv.approved_settlement_total_dkk ? `${inv.approved_settlement_total_dkk.toLocaleString("da-DK")} DKK` : "ikke godkendt endnu"}/>
          <Row k="Match" v={inv.matches_approved_settlement === true ? "✅ matcher" : inv.matches_approved_settlement === false ? "⚠ matcher ikke" : "—"}/>
          <button onClick={downloadPdf} className="px-4 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm inline-flex items-center gap-2"
                  data-testid="vognmand-invoice-download">
            <Download size={14}/> Download PDF
          </button>
          {inv.status === "pending_review" && (
            <div className="bg-brand-bg2 border border-brand p-4 space-y-3">
              <textarea className="form-input w-full px-3 py-2 border border-brand text-sm" rows={2}
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Begrundelse (kræves ved afvis)"
                        data-testid="vognmand-invoice-reason"/>
              <div className="flex gap-2">
                <button onClick={() => decide("approve")} disabled={busy}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="vognmand-invoice-approve">✅ Godkend</button>
                <button onClick={() => decide("reject")} disabled={busy}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50"
                        data-testid="vognmand-invoice-reject">❌ Afvis</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
